import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import PublicNavigation from '../../components/ui/PublicNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { professionalClientAuthService } from '../../services/professionalClientAuthService';

const ProfessionalClientRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState(0);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    clientType: 'other'
  });

  // Countdown timer effect for rate limit
  React.useEffect(() => {
    if (retryCountdown > 0) {
      const timer = setTimeout(() => {
        setRetryCountdown(retryCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (retryCountdown === 0 && rateLimited) {
      setRateLimited(false);
    }
  }, [retryCountdown, rateLimited]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e?.target?.name]: e?.target?.value
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Check if rate limited
    if (rateLimited && retryCountdown > 0) {
      toast?.error(`Veuillez patienter encore ${Math.ceil(retryCountdown / 60)} minute(s) avant de réessayer.`, {
        duration: 3000
      });
      return;
    }

    // Validation
    if (!formData?.companyName || !formData?.contactName || !formData?.email || !formData?.password) {
      toast?.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (formData?.password !== formData?.confirmPassword) {
      toast?.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData?.password?.length < 6) {
      toast?.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      setLoading(true);
      await professionalClientAuthService?.signUp({
        companyName: formData?.companyName,
        contactName: formData?.contactName,
        email: formData?.email,
        phone: formData?.phone,
        password: formData?.password,
        clientType: formData?.clientType
      });

      toast?.success('Compte créé avec succès !', {
        duration: 3000
      });
      setSuccess(true);
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle rate limit error specifically
      if (error?.code === 'RATE_LIMIT') {
        setRateLimited(true);
        setRetryCountdown(error?.retryAfter || 300); // Default 5 minutes
        toast?.error(error?.message, {
          duration: 8000,
          style: {
            maxWidth: '600px'
          }
        });
      } else {
        // Show other error messages
        const errorMessage = error?.message || 'Erreur lors de la création du compte';
        toast?.error(errorMessage, {
          duration: 5000,
          style: {
            maxWidth: '500px'
          }
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" />
      <PublicNavigation />
      <div className="flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-2xl">
          <div className="bg-white rounded-lg shadow-lg p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <Icon name="UserPlus" size={32} className="text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Créer un compte professionnel</h1>
              <p className="text-gray-600 mt-2">Accédez à votre espace dédié avec suivi complet</p>
            </div>

            {/* Registration Form */}
            {success ? (
              <div className="text-center py-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <Icon name="CheckCircle" size={32} className="text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Compte créé avec succès !</h2>
                <p className="text-gray-600 mb-6">
                  Votre compte professionnel a bien été créé. Vous pouvez maintenant vous connecter à votre espace client.
                </p>
                <Link
                  to="/professional-client-login"
                  className="inline-block bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
                >
                  Se connecter
                </Link>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Rate limit warning banner */}
              {rateLimited && retryCountdown > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5 mr-3" />
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-yellow-800 mb-1">
                        Limite de tentatives atteinte
                      </h3>
                      <p className="text-sm text-yellow-700">
                        Veuillez patienter <strong>{Math.floor(retryCountdown / 60)}:{String(retryCountdown % 60)?.padStart(2, '0')}</strong> avant de réessayer.
                      </p>
                      <p className="text-xs text-yellow-600 mt-2">
                        Cette mesure de sécurité protège votre compte. Si vous avez besoin d'aide immédiate, contactez notre support.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de l'entreprise *
                  </label>
                  <Input
                    id="companyName"
                    name="companyName"
                    type="text"
                    value={formData?.companyName}
                    onChange={handleChange}
                    placeholder="Votre entreprise"
                    required
                    disabled={loading}
                  />
                </div>

                <div>
                  <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom du contact *
                  </label>
                  <Input
                    id="contactName"
                    name="contactName"
                    type="text"
                    value={formData?.contactName}
                    onChange={handleChange}
                    placeholder="Votre nom"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email professionnel *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData?.email}
                    onChange={handleChange}
                    placeholder="contact@entreprise.com"
                    required
                    disabled={loading}
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData?.phone}
                    onChange={handleChange}
                    placeholder="+33 6 12 34 56 78"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="clientType" className="block text-sm font-medium text-gray-700 mb-2">
                  Type de client
                </label>
                <select
                  id="clientType"
                  name="clientType"
                  value={formData?.clientType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  disabled={loading}
                >
                  <option value="merchant">Commerçant / Importateur</option>
                  <option value="association">Association / ONG</option>
                  <option value="company">Entreprise / Envois réguliers</option>
                  <option value="moving">Déménagement complet</option>
                  <option value="other">Autre</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Mot de passe *
                  </label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData?.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 6 caractères</p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmer le mot de passe *
                  </label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData?.confirmPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Création du compte...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <Icon name="UserPlus" size={20} />
                    <span>Créer mon compte pro</span>
                  </div>
                )}
              </Button>
            </form>
            )}

            {/* Login Link */}
            {!success && (
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Vous avez déjà un compte ?{' '}
                <Link
                  to="/professional-client-login"
                  className="text-green-600 hover:text-green-700 font-medium"
                >
                  Se connecter
                </Link>
              </p>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalClientRegister;